import { Button, Grid, TextField, LinearProgress, Backdrop } from "@mui/material";
import { logger } from "core/utils/logger";
import { memo, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { removeEmptyValuesFromObject } from "utils";
import { useSnackbar } from "notistack";
import { format, parse } from "date-fns";
import { MobileDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { addNewBooking, updateBooking } from "services/bookings";
import { useSearchParams } from "react-router-dom";
import { getPGDefaults } from "services/businesseCenters";

const defaultFormFields = { name: '', mobileNumber:'', email: '', joiningDate: null, bookingAmount: '', bedId: '', comments: '', bedInfo: null};

export default memo(function AddBooking({closeModal, businessId, onBookingAdded, bookingData, bedSelection, openModal=false}) {
  logger.info('[AddBooking] mounting...');

  const { enqueueSnackbar } = useSnackbar();
  const [requestProcessing, setRequestProcessing] = useState(false);
  const { handleSubmit, control, formState: { errors }, reset, watch, setValue } = useForm({
    mode: 'onTouched',
    defaultValues: {...defaultFormFields}
  });
  const [urlSearchParams] = useSearchParams();
  const bedDetailsQP = urlSearchParams.get('bs');

  useEffect(() => {
    if(bookingData){
      const { joiningDate } = bookingData;
      reset({...bookingData, ...(joiningDate ? {joiningDate: parse(joiningDate, 'dd-MM-yyyy', new Date())} : {}) });
    }else if(bedSelection) {
      const { bedId, bedInfo=null } = bedSelection;
      reset({...defaultFormFields, bedId, bedInfo: bedInfo });
    }else{
      const bedDetailsQPObjStr = decodeURIComponent(bedDetailsQP);
      try {
        const bedDetails = JSON.parse(bedDetailsQPObjStr);
        const { bedId, bedInfo=null } = bedDetails;
        reset({...defaultFormFields, bedId, bedInfo: bedInfo });
      }catch(e) {
        reset({...defaultFormFields});
      }
    }
  }, [reset, bookingData, bedDetailsQP, bedSelection]);

  useEffect(() => {
    if (!openModal || bookingData || !businessId) return undefined;

    const controller = new AbortController();
    getPGDefaults({businessId}, {signal: controller.signal}).then(({response, error}) => {
      if (error?.message === 'canceled') return;
      if (error) {
        enqueueSnackbar(error?.message, {variant: 'error'});
        return;
      }
      if(response && Object.keys(response).length > 0 && response.booking){
        setValue('bookingAmount', response.booking)
      }
    });

    return () => {
      controller.abort();
    };
  }, [bookingData, businessId, enqueueSnackbar, openModal, setValue]);

  function onSubmit(formData){
    const data = removeEmptyValuesFromObject(formData);
    delete data['bedInfo'];
    const postData = {...data, joiningDate: format(data.joiningDate, 'dd-MM-yyyy')};
    if(data.id){
      updateBookingDetails(postData);
    }else{
      saveBookingDetails(postData);
    }
  }
  
  function saveBookingDetails(data) {
    logger.debug('[AddBooking]:{saveBookingDetails} saving data');
    setRequestProcessing(true);
    addNewBooking(businessId, data, {backgroundRequest: true}).then(({error}) => {
    setRequestProcessing(false);
      // Return if any error
      if(error)return enqueueSnackbar(error?.message, {variant: 'error'});

      enqueueSnackbar('New booking has been added', {variant: 'success'});
      reset({...defaultFormFields});
      typeof onBookingAdded === 'function' && onBookingAdded()
      closeModal();
    });
  }

  function updateBookingDetails(data) {
    logger.debug('[AddBooking]:{updateBookingDetails} saving data');
    setRequestProcessing(true);
    updateBooking(businessId, data, {backgroundRequest: true}).then(({error}) => {
    setRequestProcessing(false);
      // Return if any error
      if(error)return enqueueSnackbar(error?.message, {variant: 'error'});

      enqueueSnackbar('Booking details updated successfully', {variant: 'success'});
      reset({...defaultFormFields});
      typeof onBookingAdded === 'function' && onBookingAdded()
      closeModal();
    });
  }

  return (
    <>
      <Backdrop sx={{position: 'absolute', zIndex: 1}} open={requestProcessing}>
        <LinearProgress color="primary" sx={{position: 'absolute', width: '100%', top: 0, left:0}} />
      </Backdrop>
      
      <Grid container alignItems="center" rowSpacing={1} noValidate component="form" onSubmit={handleSubmit(onSubmit)}>
        <Grid  item xs={12} sm={12} md={12} lg={12} xl={12} >
          <Controller
            control={control}
            name="name"
            id="name"
            rules={{ required: "Name is required" }}
            render={({field}) => (
              <TextField
                {...field}
                margin="normal"
                label="Name"
                error={!!errors?.name}
                fullWidth 
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <Controller
            control={control}
            name="mobileNumber"
            id="mobileNumber"
            rules={{ required: "You must provide a mobile number", 
                  pattern: {
                    value: /^[0-9]{10}$/i,
                    message: "Enter valid mobile number"
                  }
                }}
            render={({field}) => (
              <TextField
                {...field}
                error={!!errors?.mobileNumber}
                helperText={(errors?.mobileNumber?.message) || ''}
                margin="normal"
                label="Mobile Number"
                fullWidth
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <Controller
            control={control}
            name="email"
            id="email"
            rules={{
              required: false,
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: "Invalid email address"
              }
            }}
            render={({field}) => (
              <TextField
                {...field}
                value={field.value || ''}
                type="email"
                margin="normal"
                label="Email ID"
                helperText={(errors?.email?.message) || ''}
                error={!!errors?.email}
                fullWidth
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={12} md={6} mt={1.5}>
          <Controller
            control={control}
            name="joiningDate"
            id="joiningDate"
            required
            rules={{
              required: true 
              // validate: (value) => !(isBefore(value, startOfDay(sub(new Date(), {months: 6})))) || "Past dates are not allowed",
              // validate: (value) => value?.getTime() >= watch('validityStartDate').getTime()  || "Validity End Date should be greater than Validity Start Date ",
            }}
            render={({field}) => (
              <LocalizationProvider dateAdapter={AdapterDateFns} >
                <MobileDatePicker
                  {...field}
                  required
                  label="Joining Date"
                  format="dd-MMM-yyyy"
                  clearable={false}
                  disablePast // Need to enable after back date entries done
                  // minDate={field?.value || new Date()} // Need to disable after back date entries done
                  value={field.value}
                  onChange={(value) => {
                    field.onChange(value);
                  }}
                  closeOnSelect={true}
                  openTo="day"
                  views={['year', 'month', 'day']}
                  textField={(params) => (
                    <TextField 
                      value={field.value}
                      {...params} 
                      margin="normal" 
                      sx={{width: '100%'}}
                    />
                  )}
                  slotProps={{
                    actionBar: {
                      actions: ['today'],
                    },
                    textField: {
                      error: !!(errors?.joiningDate),
                    }
                  }}
                />
              </LocalizationProvider>
            )}
          />
        </Grid>

        <Grid item xs={12} sm={12} md={6} mt={1.5}>
          <Controller
            control={control}
            name="bookingAmount"
            id="bookingAmount"
            render={({field}) => (
              <TextField
                {...field}
                type="number"
                onWheel={(e) => e.target.blur()}
                label="Booking Amount"
                fullWidth
              />
            )}
          />
        </Grid>
        {watch('bedInfo') ? (
          <Grid  item xs={12} sm={12} md={12} lg={12} xl={12} >
            <Controller
              control={control}
              name="bedInfo"
              id="bedInfo"
              render={({field}) => (
                <TextField
                {...field}
                  disabled
                  margin="normal"
                  label="Bed Info"
                  fullWidth
                />
              )}
            />
          </Grid>
        ): null}
        <Grid  item xs={12} sm={12} md={12} lg={12} xl={12} >
          <Controller
            control={control}
            name="comments"
            id="comments"
            render={({field}) => (
              <TextField
                {...field}
                value={field.value || ''}
                margin="normal"
                label="Comments"
                fullWidth
                multiline
                minRows={3}
              />
            )}
          />
        </Grid>
        
        <Grid item xs={12} sm={12} md={12} lg={12} textAlign="center">
          <Button
            type="submit"
            variant="contained"
            color="primary"
            sx={{mt:3}}
            >
            {bookingData ? 'Update' : 'Add'}
          </Button>
        </Grid>
      </Grid>
    </>
  )
});
